import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from 'firebase/auth';
import { UserDataType } from '../../firebaseApp';
import { SongType } from '../helpers/typings';

export type ValueOf<T> = T[keyof T]

// 'redeeming' while a login nonce handed over by the mobile app is being
// exchanged for a session; set synchronously on client entry, before React
// first renders, so access gates can wait instead of flashing a signed-out
// state. 'failed' if the exchange throws, so the UI can tell the user rather
// than silently falling back to signed-out content. See redeemLoginNonceFromUrl.
export type LoginNonceStatus = 'idle' | 'redeeming' | 'failed';

export interface SiteState {
  // The live Firebase auth user. Maintained by the module-level
  // onAuthStateChanged listener in firebaseApp so that it survives page
  // navigation and is read synchronously (see useUser). Never persisted.
  user: User | null;
  userData: UserDataType | null;
  userPreviouslyLoggedIn: boolean;
  loginNonceStatus: LoginNonceStatus;
  showDownloadPopup: boolean;
  showFailureToOpenAppDialog: boolean;
  showLoginPopup: boolean;
  showSignupPopup: boolean;
  showDownloadPopupAfterSignup: boolean;
  showNeedsAccountSetupDialog: boolean;
  isInProcessOfSigningUp: boolean;
  showPasswordResetDialog: boolean;
  needsEmailVerification: boolean;
  reasonToLogIn: string | null;
  openAppAfterLogin: boolean;
  redirectAfterLogin: string | null;
  needsReauthentication: boolean;
  currentSong: SongType | null;
  globalSearch: string | null;
  globalSearchHasResults: boolean;
  changeStoreState: (propertyName: keyof SiteState, propertyValue: ValueOf<SiteState>) => void
}

const excludeFromPersistence: (keyof SiteState)[] = [
  'user',
  'userData',
  'loginNonceStatus',
  'showDownloadPopup',
  'showFailureToOpenAppDialog',
  'showLoginPopup',
  'showSignupPopup',
  'showDownloadPopupAfterSignup',
  'showNeedsAccountSetupDialog',
  'isInProcessOfSigningUp',
  'showPasswordResetDialog',
  'needsEmailVerification',
  'reasonToLogIn',
  'openAppAfterLogin',
  'redirectAfterLogin',
  'needsReauthentication',
  'currentSong',
  'globalSearch',
  'globalSearchHasResults',
];

export type SetFuncType = (
  partial: SiteState | Partial<SiteState> | ((state: SiteState) => SiteState | Partial<SiteState>),
  replace?: boolean
) => void

export const initialState: (set: SetFuncType) => SiteState = (set) => {
  return {
    user: null,
    userData: null,
    userPreviouslyLoggedIn: false,
    loginNonceStatus: 'idle',
    showDownloadPopup: false,
    showFailureToOpenAppDialog: false,
    showLoginPopup: false,
    showSignupPopup: false,
    showDownloadPopupAfterSignup: false,
    showNeedsAccountSetupDialog: false,
    isInProcessOfSigningUp: false,
    showPasswordResetDialog: false,
    needsEmailVerification: false,
    reasonToLogIn: null,
    openAppAfterLogin: false,
    redirectAfterLogin: null,
    needsReauthentication: false,
    currentSong: null,
    globalSearch: null,
    globalSearchHasResults: true,
    changeStoreState: (propertyName: keyof SiteState, propertyValue: ValueOf<SiteState>) =>
      set(() => ({ [propertyName]: propertyValue })),
  };
};

export const useStore = create<SiteState>()(
  persist((set) => initialState(set), {
    name: 'app-storage', // name of the item in the storage (must be unique)
    partialize: (state) =>
      Object.fromEntries(
        Object.entries(state).filter(([ key ]) => !excludeFromPersistence.includes(key as keyof SiteState))
      ),
  })
);
